/**
 * Client session + RBAC (M2 W3). On mount, SessionProvider fetches the authenticated
 * principal from `GET /api/v1/me` (the safe principal: name/email/role/permissions/
 * allowedNav — never a password) and exposes it via context. `useSession()` returns
 * the principal + the permission-derived allowed nav ids; `usePermission()` gates UI
 * with `hasPermission/canView/canManage` and an admin bypass (port of skySign).
 *
 * The context has a permissive DEFAULT (allowedNav = null ⇒ "show all") so components
 * render correctly even without a provider (e.g. in shell unit tests) — gating only
 * tightens once the real session loads.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { ApiProblem, fetchEnvelope } from "./api";
import { isB2cMode } from "../auth/authMode";

/**
 * Why the session load failed, as a first-class signal the AuthorizationGate acts on:
 *   - `unauthorized` (HTTP 403): a valid credential/token but NO active/entitled platform user →
 *     the AccessDenied screen (authenticated, not authorized).
 *   - `sessionExpired` (HTTP 401, b2c): the token expired/was rejected AND api.ts's silent re-acquire
 *     did not recover (it redirects on a gone B2C session) → a "sign in again" screen, NOT a dead-end.
 *   - `authRejected` (HTTP 401, b2c, TERMINAL): api.ts already retried once with a FORCE-REFRESHED
 *     token and it was STILL rejected (audience/scope/clock/provisioning). "Sign in again" would just
 *     mint another rejected token and loop, so this maps to a terminal "we could not verify your
 *     access" screen with a Sign-out (not a re-login) affordance.
 *   - `unavailable` (5xx / network / any other failure): the API could not answer → a retry screen.
 * A successful `/me` clears this to `null`.
 */
export type SessionError = "unauthorized" | "sessionExpired" | "authRejected" | "unavailable" | null;

export interface SessionPrincipal {
  userId: string;
  email: string;
  name: string;
  tenant: string;
  instance: string;
  role: string;
  isAdmin: boolean;
  permissions: Record<string, boolean>;
  allowedNav: string[];
}

interface SessionState {
  principal: SessionPrincipal | null;
  /** Allowed nav ids, or null when unknown (not loaded / no provider) ⇒ show all. */
  allowedNav: string[] | null;
  loading: boolean;
  /**
   * Non-null when the `/me` load FAILED. The AuthorizationGate reads this to stop an
   * authenticated-but-unauthorized user at the door instead of degrading to "show all nav".
   */
  error: SessionError;
}

const SessionContext = createContext<SessionState>({
  principal: null,
  allowedNav: null,
  loading: false,
  error: null,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({
    principal: null,
    allowedNav: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    fetchEnvelope<SessionPrincipal>("/api/v1/me")
      .then(({ data }) => {
        if (cancelled) return;
        // Validate a MINIMUM principal shape before treating the load as resolved. A partial/empty
        // 200 body (e.g. `{}`) would otherwise set allowedNav to null ⇒ "show all nav" (fail-open).
        // Require the identifying fields (userId + role); anything less fails CLOSED to the retry
        // screen (error 'unavailable') rather than rendering an unauthorized-but-permissive shell.
        if (
          !data ||
          typeof data.userId !== "string" ||
          data.userId.length === 0 ||
          typeof data.role !== "string" ||
          data.role.length === 0
        ) {
          setState({ principal: null, allowedNav: null, loading: false, error: "unavailable" });
          return;
        }
        setState({
          principal: data,
          allowedNav: data.allowedNav ?? null,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Do NOT fall back to "show all nav" — that is fail-open. Map the HTTP status so the
        // AuthorizationGate gates on AUTHORIZATION: 403 ⇒ authenticated-but-not-authorized
        // (AccessDenied); a 401 in b2c mode that survived api.ts's silent re-acquire ⇒ the token
        // expired/was rejected without a redirect (sessionExpired ⇒ "sign in again", never a
        // dead-end); everything else (5xx / network / parse) ⇒ transient unavailable (retry).
        // No PHI is logged here.
        const status = err instanceof ApiProblem ? err.status : 0;
        // A fresh (force-refreshed) token STILL rejected by the API: terminal, not a re-login loop.
        const authRejected = err instanceof ApiProblem && err.authRejected === true;
        // A missing MSAL account (evicted mid-session) surfaces as AuthAccountMissingError from the
        // token step BEFORE the fetch — checked by name so this basic-mode-safe module never static-
        // imports authService (which would pull MSAL into the basic bundle). Treat it as expired, not
        // a transient outage, so the b2c user gets "sign in again" not "temporarily unavailable".
        const noAccount = err instanceof Error && err.name === "AuthAccountMissingError";
        let error: SessionError;
        if (status === 403) error = "unauthorized";
        // A retried-fresh-token 401 is checked BEFORE the ordinary-401 branch so it never degrades to
        // the re-login screen that would loop. b2c-only (the flag is only ever set in b2c mode).
        else if (authRejected && isB2cMode()) error = "authRejected";
        else if ((status === 401 || noAccount) && isB2cMode()) error = "sessionExpired";
        else error = "unavailable";
        setState({ principal: null, allowedNav: null, loading: false, error });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return <SessionContext.Provider value={state}>{children}</SessionContext.Provider>;
}

/** The current session state (principal + allowedNav + loading). */
export function useSession(): SessionState {
  return useContext(SessionContext);
}

/** RBAC helpers: hasPermission/canView/canManage with an admin bypass. */
export function usePermission() {
  const { principal } = useSession();
  const isAdmin = principal?.isAdmin ?? false;
  const permissions = principal?.permissions ?? {};

  const hasPermission = (key: string): boolean => {
    if (isAdmin) return true;
    return permissions[key] === true;
  };
  const canView = (module: string): boolean =>
    hasPermission(`view${module}`) || hasPermission(`manage${module}`);
  const canManage = (module: string): boolean => hasPermission(`manage${module}`);

  return { hasPermission, canView, canManage, permissions, isAdmin, principal };
}
